import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import type { PermissionValue } from '@app/core/services';
import { AuthStore } from '../auth/auth.store';
import { CurrentUserService } from '../services';

const FALLBACK_ROUTES: readonly {
  readonly permissionPrefixes: readonly string[];
  readonly route: string;
}[] = [
  { permissionPrefixes: ['PERM_USER_CHAT_'], route: '/dashboard' },
  { permissionPrefixes: ['PERM_MODULE_CHATBOT_'], route: '/settings/chatbots' },
  { permissionPrefixes: ['PERM_MODULE_LIVE_CHAT_SETTINGS_'], route: '/settings/live-chat' },
  { permissionPrefixes: ['PERM_MODULE_REPORTING_'], route: '/settings/reports' },
  { permissionPrefixes: ['PERM_TENANT_COMPANY_'], route: '/settings/general' },
  {
    permissionPrefixes: ['PERM_TENANT_USER_', 'PERM_TENANT_ROLE_', 'PERM_TENANT_TEAM_'],
    route: '/settings/usermanagement',
  },
  {
    permissionPrefixes: ['PERM_TENANT_LICENSE_', 'PERM_TENANT_BILLING_'],
    route: '/settings/subscription',
  },
  { permissionPrefixes: ['PERM_TENANT_INTEGRATION_'], route: '/settings/integrations' },
  { permissionPrefixes: ['PERM_PLATFORM_TENANT_'], route: '/support/tenants' },
  { permissionPrefixes: ['PERM_PLATFORM_CONTRACT_', 'PERM_PLATFORM_ORDER_'], route: '/support/contracts' },
  { permissionPrefixes: ['PERM_PLATFORM_SCHEDULED_TASK_'], route: '/system/scheduledtasks' },
  { permissionPrefixes: ['PERM_PLATFORM_INTEGRATION_'], route: '/system/integrationstore' },
  { permissionPrefixes: ['PERM_PLATFORM_CONFIGURATION_'], route: '/system/configurations' },
  { permissionPrefixes: ['PERM_PLATFORM_LICENSE_'], route: '/system/paymentsettings' },
];

export const permissionGuard: CanActivateFn = async (route, state) => {
  const authStore = inject(AuthStore);
  const currentUserService = inject(CurrentUserService);
  const router = inject(Router);

  if (!authStore.isAuthenticated()) {
    return router.createUrlTree(['/login'], {
      queryParams: { returnUrl: state.url },
    });
  }

  const hasCurrentUser = await currentUserService.ensureCurrentUserLoaded();
  if (!hasCurrentUser) {
    return router.createUrlTree(['/login'], {
      queryParams: { returnUrl: state.url },
    });
  }

  const permissionsData = route.data?.['permissions'];
  const requiredPermissions = Array.isArray(permissionsData)
    ? permissionsData.filter((value): value is PermissionValue => typeof value === 'string')
    : [];
  const permissionPrefixesData = route.data?.['permissionPrefixes'];
  const requiredPermissionPrefixes = Array.isArray(permissionPrefixesData)
    ? permissionPrefixesData.filter((value): value is string => typeof value === 'string')
    : [];
  const rolesData = route.data?.['roles'];
  const requiredRoles = Array.isArray(rolesData)
    ? rolesData.filter((value): value is string => typeof value === 'string')
    : [];

  if (
    requiredPermissions.length === 0 &&
    requiredPermissionPrefixes.length === 0 &&
    requiredRoles.length === 0
  ) {
    return true;
  }

  const hasPermission =
    (requiredPermissions.length > 0 && authStore.hasAnyPermission(requiredPermissions)) ||
    (requiredPermissionPrefixes.length > 0 &&
      authStore.hasAnyPermissionIn(requiredPermissionPrefixes));
  if (
    (requiredPermissions.length > 0 || requiredPermissionPrefixes.length > 0) &&
    !hasPermission
  ) {
    return createFallbackUrlTree(authStore, router);
  }

  if (requiredRoles.length > 0 && !authStore.hasAnyRole(requiredRoles)) {
    return createFallbackUrlTree(authStore, router);
  }

  return true;
};

function createFallbackUrlTree(authStore: AuthStore, router: Router) {
  const fallback = FALLBACK_ROUTES.find((item) =>
    authStore.hasAnyPermissionIn(item.permissionPrefixes),
  );
  if (fallback) {
    return router.createUrlTree([fallback.route]);
  }

  return router.createUrlTree(['/login'], { queryParams: { logout: '1' } });
}
